import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} CartNotificationRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLHeadingElement} itemTitle - The item title element
 * @property {HTMLSpanElement} itemCountElement - The item title element
 * @property {HTMLDivElement} itemVariant - The item variant element
 * @property {HTMLSpanElement} itemPrice - The item price element
 * @property {HTMLImageElement} itemImage - The item image element
 * @property {HTMLSpanElement} cartCount - The cart count element
 * @property {HTMLButtonElement} closeButton - The cart count element
*/


class CartNotificationComponent extends DialogComponent {
  static eventName = 'cart:add';

   /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {CartNotificationRefs}
   */
  refs;

  requiredRefs = [
    'dialog',
    'itemTitle',
    'itemCountElement',
    'itemVariant',
    'itemPrice',
    'itemImage',
    'cartCount',
  ];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartNotificationComponent.eventName, this.#onAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartNotificationComponent.eventName, this.#onAdd);
  }

  /**
   * Safely close dialog with animation + scroll unlock
   */
  handleClose = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    dialog.classList.add('dialog-closing');

    const onAnimationEnd = () => {
      dialog.close?.();
      dialog.classList.remove('dialog-closing');
      this.clearDialog();

      // Unlock page scroll
      document.documentElement.removeAttribute('scroll-lock');

      dialog.removeEventListener('animationend', onAnimationEnd);
    };

    dialog.addEventListener('animationend', onAnimationEnd, { once: true });
  };

  clearDialog = () => {
    const { itemTitle, itemCountElement, itemVariant, itemPrice, itemImage, cartCount } = this.refs;
    itemTitle.textContent = '';
    itemCountElement.textContent = '(1)';
    itemVariant.textContent = '';
    itemPrice.textContent = '';
    itemImage.src = '';
    itemImage.alt = '';
    itemImage.srcset = '';
    cartCount.textContent = '(0)';
  }

  #handleOpenDialog = () => {
    this.refs.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
  }


  /**
   * @param {CustomEvent<{item: ShopifyCartItem, cart: ShopifyCart, itemCount: number}>} event
   */
  #onAdd = (event) => {
    const { item, cart, itemCount } = event.detail || {};
    const { itemTitle, itemCountElement, itemVariant, itemPrice, itemImage, cartCount } = this.refs;

    itemTitle.textContent = item.product_title || item?.title || '';
    itemCountElement.textContent = itemCount ? `(${itemCount})` : '(1)';
    itemVariant.textContent = item?.variant_title || '';
    itemPrice.textContent = `$${(item?.price / 100 || 0).toFixed(2)}`;

    if (itemImage && item.image) {
      const imageUrl = item.image.includes('?') 
        ? `${item.image}&width=160` 
        : `${item.image}?width=160`;
      
      itemImage.src = imageUrl;
      itemImage.alt = item.title || '';
      
      // Add srcset for retina displays
      const imageUrl2x = item.image.includes('?')
        ? `${item.image}&width=320`
        : `${item.image}?width=320`;
      
      this.refs.itemImage.srcset = `${imageUrl} 1x, ${imageUrl2x} 2x`;
    }

    if (cartCount) {
      cartCount.textContent = `(${cart?.item_count ?? 0})`;
    }

    this.#handleOpenDialog();
  };
}

if (!customElements.get('cart-notification-component')) {
  customElements.define('cart-notification-component', CartNotificationComponent);
}

 /**
   * A single line item inside the Shopify cart.
   * Matches objects inside `cartData.items`.
   *
   * @typedef {Object} ShopifyCartItem
   * @property {number} id
   * @property {number} variant_id
   * @property {string} key
   * @property {string} title
   * @property {number} quantity
   * @property {number} price
   * @property {number} original_price
   * @property {number} discounted_price
   * @property {number} line_price
   * @property {number} original_line_price
   * @property {number} total_discount
   * @property {string} sku
   * @property {number} grams
   * @property {string} vendor
   * @property {boolean} taxable
   * @property {number} product_id
   * @property {boolean} product_has_only_default_variant
   * @property {boolean} gift_card
   * @property {number} final_price
   * @property {number} final_line_price
   * @property {string} url
   * @property {{ aspect_ratio:number, alt:string|null, height:number, url:string, width:number }} featured_image
   * @property {string} image
   * @property {string} handle
   * @property {boolean} requires_shipping
   * @property {string} product_type
   * @property {string} product_title
   * @property {string} product_description
   * @property {string} variant_title
   * @property {string[]} variant_options
   * @property {{name:string, value:string}[]} options_with_values
   */

  /**
   * The `/cart.js` payload.
   *
   * @typedef {Object} ShopifyCart
   * @property {string} token
   * @property {string} note
   * @property {Object<string, any>} attributes
   * @property {number} original_total_price
   * @property {number} total_price
   * @property {number} total_discount
   * @property {number} total_weight
   * @property {number} item_count
   * @property {ShopifyCartItem[]} items
   * @property {boolean} requires_shipping
   * @property {string} currency
   * @property {number} items_subtotal_price
   * @property {any[]} cart_level_discount_applications
   * @property {any[]} discount_codes
   */